import { v4 as uuidv4 } from 'uuid';

import type { GraphEdge, GraphNode, IGraph } from '@chainlit/react-client';

import type { GraphSnapshot } from '@/state/graphHistory';

/** A reconstructed STIX 2.1 bundle. */
export interface StixBundle {
  type: 'bundle';
  id: string;
  objects: Record<string, unknown>[];
}

interface BuildOptions {
  now?: string;
  bundleId?: string;
}

/**
 * Rebuild a STIX 2.1 bundle from graph nodes/edges — the inverse of the backend
 * `create_chainlit_stix_graph`. Each node carries the STIX object's fields in
 * `data` (minus `id`/`name`), so an object is `{ ...data, id, name? }`; `name`
 * is restored only when it differs from the object `type`, because the forward
 * pass uses the type as a display fallback for nameless objects (SCOs). Each
 * edge encodes a relationship SRO (`name`=relationship_type, `label`=type,
 * `identifier`=id, source/target=refs); its `created`/`modified` are not carried
 * on the edge, so they are regenerated from `now`.
 */
export function buildStixBundleFromGraph(
  nodes: readonly GraphNode[] | undefined,
  edges: readonly GraphEdge[] | undefined,
  options: BuildOptions = {}
): StixBundle {
  const now = options.now ?? new Date().toISOString();
  const objects: Record<string, unknown>[] = [];

  for (const node of nodes ?? []) {
    const object: Record<string, unknown> = {
      ...(node.data ?? {}),
      id: node.identifier
    };
    if (node.name && node.name !== object.type) {
      object.name = node.name;
    }
    objects.push(object);
  }

  for (const edge of edges ?? []) {
    objects.push({
      type: edge.label || 'relationship',
      spec_version: '2.1',
      id: edge.identifier,
      created: now,
      modified: now,
      relationship_type: edge.name,
      source_ref: edge.source,
      target_ref: edge.target
    });
  }

  return {
    type: 'bundle',
    id: options.bundleId ?? `bundle--${uuidv4()}`,
    objects
  };
}

interface StixGraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

interface ResolveArgs {
  graphs: Record<string, IGraph>;
  graphHistory: Record<string, GraphSnapshot[]>;
  selectedGraphId: string;
  selectedSnapshotIndex: number | null;
}

/**
 * A node is a STIX object iff its `data.type` is a string — the STIX object type
 * (`attack-pattern`, `malware`, …). COA task nodes never carry a `type` in their
 * `data` (they hold description/status/impact/etc.), so this cleanly separates
 * STIX nodes from task nodes inside a merged Operation Overview graph. Mirrors
 * the `data.type` signal the node-details panel already uses (`Graph.tsx`).
 */
function isStixNode(node: GraphNode): boolean {
  return typeof node.data?.type === 'string';
}

function extractStixSubgraph(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[]
): StixGraphData {
  const stixNodes = nodes.filter(isStixNode);
  const ids = new Set(stixNodes.map((n) => n.identifier));
  const stixEdges = edges.filter((e) => ids.has(e.source) && ids.has(e.target));
  return { nodes: stixNodes, edges: stixEdges };
}

/**
 * Resolve the STIX nodes/edges to export from the *currently displayed* graph —
 * the selected graph at the selected timeline snapshot (mirrors `Graph.tsx`
 * `currentGraph`), then filtered to just its STIX objects. Works for both a pure
 * STIX graph and the merged Operation Overview (COA + STIX). Returns `null` when
 * the displayed snapshot contains no STIX — nothing selected, a COA-only view, or
 * an early snapshot before STIX was generated — which hides the download button.
 */
export function resolveStixExportGraph(
  args: ResolveArgs
): StixGraphData | null {
  const { graphs, graphHistory, selectedGraphId, selectedSnapshotIndex } = args;

  const live = graphs[selectedGraphId];
  if (selectedGraphId === 'none' || !live) return null;

  const snapshots = graphHistory[selectedGraphId] ?? [];
  const useSnapshot =
    snapshots.length >= 2 &&
    selectedSnapshotIndex !== null &&
    selectedSnapshotIndex >= 0 &&
    selectedSnapshotIndex < snapshots.length - 1;
  const source = useSnapshot
    ? snapshots[selectedSnapshotIndex as number]
    : live;

  const stix = extractStixSubgraph(
    (source.nodes ?? []) as GraphNode[],
    (source.edges ?? []) as GraphEdge[]
  );
  return stix.nodes.length > 0 ? stix : null;
}
